import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { formatRp } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle, XCircle, Eye } from "@phosphor-icons/react";

const PAY_BADGE = {
  "Menunggu Verifikasi": "bg-amber-100 text-amber-700",
  "Terverifikasi": "bg-emerald-100 text-emerald-700",
  "Ditolak": "bg-rose-100 text-rose-700",
  "Void": "bg-slate-200 text-slate-500",
};

export default function PaymentReviewDialog({ reservation, open, onClose, onChange }) {
  const r = reservation;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectId, setRejectId] = useState(null);
  const [reason, setReason] = useState("");

  const load = () => {
    if (!r?.id) return;
    setLoading(true);
    api.get(`/payments/reservation/${r.id}`).then((res) => setData(res.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, r?.id]);

  const verify = async (id) => {
    try { await api.post(`/payments/${id}/verify`); toast.success("Pembayaran diverifikasi"); load(); onChange && onChange(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doReject = async () => {
    if (!reason.trim()) return toast.error("Alasan wajib diisi");
    try { await api.post(`/payments/${rejectId}/reject`, { reason }); toast.success("Pembayaran ditolak"); setRejectId(null); setReason(""); load(); onChange && onChange(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const viewProof = async (fid) => {
    try {
      const res = await api.get(`/payments/proof/${fid}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  const res = data?.reservation || r || {};
  const pays = data?.payments || [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-2xl sm:max-w-lg" data-testid="payment-review-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Verifikasi Pembayaran · {res.nama_lengkap}</DialogTitle></DialogHeader>

        <div className="rounded-xl border border-[#E0D2FF] bg-[#FAF7FF] p-3 text-sm">
          <p className="mb-1 text-xs font-semibold text-slate-500">{res.jenis_layanan} · {res.tanggal_kunjungan} {res.jam_kunjungan}</p>
          <div className="flex justify-between"><span className="text-slate-500">Total Tagihan</span><span className="font-bold text-slate-800">{formatRp(res.total_tagihan)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Total Dibayar</span><span className="font-semibold text-emerald-600">{formatRp(res.total_dibayar)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Sisa Tagihan</span><span className="text-slate-700">{formatRp(res.sisa_tagihan)}</span></div>
        </div>

        {loading ? (
          <p className="py-6 text-center text-sm text-slate-400">Memuat...</p>
        ) : pays.length === 0 ? (
          <p data-testid="review-empty" className="py-6 text-center text-sm text-slate-400">Belum ada pembayaran untuk reservasi ini.</p>
        ) : (
          <div className="space-y-2">
            {pays.map((p) => (
              <div key={p.id} data-testid={`review-row-${p.id}`} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 p-3 text-sm">
                <div className="min-w-0">
                  <p className="font-bold text-slate-800">{formatRp(p.amount)} <span className="text-xs font-normal text-slate-400">· {p.method} · {p.kind}</span></p>
                  <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${PAY_BADGE[p.status] || "bg-slate-100 text-slate-500"}`}>{p.status}</span>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  {p.proof_file_id && <button data-testid={`review-proof-${p.id}`} onClick={() => viewProof(p.proof_file_id)} className="rounded-lg bg-slate-100 p-1.5 text-slate-600" title="Lihat bukti"><Eye size={16} /></button>}
                  {p.status === "Menunggu Verifikasi" && <>
                    <button data-testid={`review-verify-${p.id}`} onClick={() => verify(p.id)} className="rounded-lg bg-emerald-500 p-1.5 text-white" title="Verifikasi"><CheckCircle size={16} weight="bold" /></button>
                    <button data-testid={`review-reject-${p.id}`} onClick={() => { setRejectId(p.id); setReason(""); }} className="rounded-lg bg-rose-500 p-1.5 text-white" title="Tolak"><XCircle size={16} weight="bold" /></button>
                  </>}
                </div>
              </div>
            ))}
          </div>
        )}

        {rejectId && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
            <textarea data-testid="review-reject-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan penolakan wajib diisi" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <div className="mt-2 flex justify-end gap-2">
              <button onClick={() => setRejectId(null)} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-500">Batal</button>
              <button data-testid="review-confirm-reject" onClick={doReject} className="rounded-lg bg-rose-500 px-4 py-1.5 text-xs font-bold text-white">Tolak Pembayaran</button>
            </div>
          </div>
        )}

        <DialogFooter>
          <button onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Tutup</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
